<!-- src/components/student/StudentModules.vue -->
<template>
	<div class="view-587">
		<div class="x-47">
			<!-- Заголовок блока + Фильтр -->
			<div class="header-row">
				<div class="text-873 poppins-bold-thunder-22px">Доступные модули</div>

				<!-- Фильтр по курсам -->
				<div class="course-filter">
					<label class="filter-label">Курс:</label>
					<select
						v-model="selectedCourse"
						class="course-select"
						@change="onCourseChange"
					>
						<option value="">Все курсы</option>
						<option
							v-for="course in availableCourses"
							:key="course"
							:value="course"
						>
							{{ course }}
						</option>
					</select>
				</div>
			</div>

			<!-- Заголовки столбцов -->
			<div class="columns-header">
				<div class="text-856 poppins-medium-thunder-20px">Модули</div>
				<div class="column-spacer" aria-hidden="true"></div>
				<div class="text-855 poppins-medium-thunder-20px">Статус</div>
			</div>

			<!-- Загрузка -->
			<div v-if="loading" class="loading-state">
				<span>Загрузка модулей...</span>
			</div>

			<!-- Пустое состояние -->
			<div v-else-if="filteredModules.length === 0" class="empty-state">
				<span v-if="selectedCourse">Нет модулей для выбранного курса</span>
				<span v-else>Нет доступных модулей</span>
			</div>

			<!-- Список модулей -->
			<div v-else class="modules-list">
				<!-- Если выбран конкретный курс - показываем без группировки -->
				<template v-if="selectedCourse">
					<div
						v-for="(module, index) in flatFilteredModules"
						:key="module.id || index"
						class="module-row"
					>
						<!-- Название и описание -->
						<p class="module-text poppins-normal-thunder-17px">
							<span class="span-24 poppins-normal-thunder-17px">
                {{ module.name }}
							</span>
						</p>

						<!-- Дата -->
						<div class="module-date module-date--spacer" aria-hidden="true">
							&nbsp;
						</div>

						<!-- Статус / Кнопка -->
						<div class="module-status">
							<template v-if="isModuleEmpty(module)">
								<div class="status-btn status-btn--locked">
									<div class="btn-bg btn-bg--gray"></div>
									<span class="btn-text poppins-bold-white-18px">Пусто</span>
								</div>
							</template>
							<template v-else-if="module.status === 'available'">
								<router-link
									:to="getModuleLink(module)"
									class="status-btn status-btn--available"
									@click="scrollToTop"
								>
									<div class="btn-bg btn-bg--green"></div>
									<span class="btn-text poppins-bold-white-18px">Перейти</span>
								</router-link>
							</template>
							<template v-else>
								<div class="status-btn status-btn--locked">
									<div class="btn-bg btn-bg--gray"></div>
									<span class="btn-text poppins-bold-white-18px">Закрыт</span>
								</div>
							</template>
						</div>
					</div>
				</template>

				<!-- Если "Все курсы" - показываем с группировкой -->
				<template v-else>
					<div
						v-for="(group, groupIndex) in filteredModules"
						:key="group.courseName || groupIndex"
						class="course-group"
					>
						<!-- Заголовок курса -->
						<div class="course-header">
							<span class="course-icon">📚</span>
							<span class="course-name">{{ group.courseName }}</span>
							<span class="course-count">({{ group.items.length }})</span>
						</div>

						<!-- Модули курса -->
						<div
							v-for="(module, index) in group.items"
							:key="module.id || index"
							class="module-row"
						>
							<!-- Название и описание -->
							<p class="module-text poppins-normal-thunder-17px">
								<span class="span-24 poppins-normal-thunder-17px">
									{{ module.name }}
								</span>
							</p>

							<!-- Дата -->
							<div class="module-date module-date--spacer" aria-hidden="true">
								&nbsp;
							</div>

							<!-- Статус / Кнопка -->
							<div class="module-status">
								<template v-if="isModuleEmpty(module)">
									<div class="status-btn status-btn--locked">
										<div class="btn-bg btn-bg--gray"></div>
										<span class="btn-text poppins-bold-white-18px">Пусто</span>
									</div>
								</template>
								<template v-else-if="module.status === 'available'">
									<router-link
										:to="getModuleLink(module)"
										class="status-btn status-btn--available"
										@click="scrollToTop"
									>
										<div class="btn-bg btn-bg--green"></div>
										<span class="btn-text poppins-bold-white-18px"
											>Перейти</span
										>
									</router-link>
								</template>
								<template v-else>
									<div class="status-btn status-btn--locked">
										<div class="btn-bg btn-bg--gray"></div>
										<span class="btn-text poppins-bold-white-18px">Закрыт</span>
									</div>
								</template>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'StudentModules',

	props: {
		/**
		 * Массив групп модулей
		 * Формат: [{ courseName: 'Курс 1', items: [...] }, ...]
		 */
		modules: {
			type: Array,
			default: () => [],
		},
		/** Состояние загрузки */
		loading: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			selectedCourse: '',
		}
	},

	computed: {
		/**
		 * Список уникальных курсов для фильтра
		 */
		availableCourses() {
			if (!this.modules || !Array.isArray(this.modules)) {
				return []
			}

			return this.modules
				.filter(group => {
					if (
						!group ||
						!group.courseName ||
						this.isGroupPaused(group) ||
						this.isGroupCompleted(group)
					)
						return false
					return this.getVisibleItems(group).length > 0
				})
				.map(group => group.courseName)
				.sort((a, b) => a.localeCompare(b))
		},

		/**
		 * Отфильтрованные модули (группы)
		 */
		filteredModules() {
			if (!this.modules || !Array.isArray(this.modules)) {
				return []
			}

			if (!this.selectedCourse) {
				// Показать модули всех курсов
				return this.modules
					.map(group => {
						if (!group) return null
						const items = this.getVisibleItems(group)
						if (!items.length) return null
						return { ...group, items }
					})
					.filter(
						group =>
							group &&
							!this.isGroupPaused(group) &&
							!this.isGroupCompleted(group),
					)
			}

			// Фильтрация по выбранному курсу
			return this.modules
				.map(group => {
					if (!group || group.courseName !== this.selectedCourse) return null
					const items = this.getVisibleItems(group)
					if (!items.length) return null
					return { ...group, items }
				})
				.filter(
					group =>
						group &&
						!this.isGroupPaused(group) &&
						!this.isGroupCompleted(group),
				)
		},

		/**
		 * Плоский список модулей для выбранного курса
		 */
		flatFilteredModules() {
			if (!this.selectedCourse) {
				return []
			}

			const group = this.modules.find(g => g.courseName === this.selectedCourse)
			if (!group || this.isGroupPaused(group) || this.isGroupCompleted(group)) {
				return []
			}
			return this.getVisibleItems(group)
		},
	},

	methods: {
		isModuleHidden(module) {
			if (!module) return false
			if (module.isHidden === true) return true
			if (module.hidden === true) return true
			if (module.is_hidden === true || module.is_draft === true) return true
			const rawStatus = String(module.status || '')
				.trim()
				.toLowerCase()
			return ['hidden', 'draft'].includes(rawStatus)
		},

		getVisibleItems(group) {
			if (!group || !Array.isArray(group.items)) return []
			return group.items.filter(item => !this.isModuleHidden(item))
		},

		onCourseChange() {
			this.$emit('course-filter-change', this.selectedCourse)
		},

		isPausedStatus(value) {
			if (!value && value !== 0) return false
			const normalized = String(value).trim().toLowerCase()
			return [
				'paused',
				'on_hold',
				'on-hold',
				'hold',
				'stopped',
				'suspended',
			].includes(normalized)
		},

		isCompletedStatus(value) {
			if (!value && value !== 0) return false
			const normalized = String(value).trim().toLowerCase()
			return ['completed', 'done', 'finished', 'passed'].includes(normalized)
		},

		toNumber(value) {
			const parsed = Number(value)
			return Number.isFinite(parsed) ? parsed : null
		},

		isCoursePaused(entity) {
			if (!entity) return false
			if (entity.isPaused === true || entity.paused === true) return true
			if (entity.coursePaused === true || entity.course_paused === true)
				return true

			const status =
				entity.enrollmentStatus ??
				entity.enrollment_status ??
				entity.courseStatus ??
				entity.course_status ??
				null
			return this.isPausedStatus(status)
		},

		isCourseCompleted(entity) {
			if (!entity) return false
			if (entity.is_completed === true || entity.isCompleted === true) return true
			if (entity.completed === true) return true
			if (entity.completed_at || entity.completedAt) return true

			const status =
				entity.enrollmentStatus ??
				entity.enrollment_status ??
				entity.courseStatus ??
				entity.course_status ??
				entity.status ??
				entity.state ??
				null

			if (this.isCompletedStatus(status)) return true

			const progress = this.toNumber(
				entity.progressPercent ??
					entity.progress_percent ??
					entity.progress ??
					null,
			)
			const currentLesson = this.toNumber(
				entity.currentLesson ??
					entity.current_lesson ??
					entity.current_lesson_number ??
					null,
			)
			const totalLessons = this.toNumber(
				entity.totalLessons ??
					entity.lessons_total ??
					entity.total_lessons ??
					null,
			)

			if (
				totalLessons &&
				currentLesson !== null &&
				currentLesson >= totalLessons
			) {
				return true
			}
			if (progress !== null && progress >= 100) return true
			return false
		},

		isGroupPaused(group) {
			if (!group) return false
			if (this.isCoursePaused(group)) return true
			const sampleItem = Array.isArray(group.items)
				? group.items.find(item => item)
				: null
			return this.isCoursePaused(sampleItem)
		},

		isGroupCompleted(group) {
			if (!group) return false
			if (this.isCourseCompleted(group)) return true
			const sampleItem = Array.isArray(group.items)
				? group.items.find(item => item)
				: null
			return this.isCourseCompleted(sampleItem)
		},

		isModuleEmpty(module) {
			if (!module) return false
			if (typeof module.isEmpty === 'boolean') return module.isEmpty

			const counts = module.contentCounts || {}
			const values = Object.values(counts).filter(
				value => value !== null && value !== undefined,
			)
			if (!values.length) return false

			return values.every(value => Number(value) === 0)
		},

		truncate(text, maxLength) {
			if (!text) return ''
			if (text.length <= maxLength) return text
			return text.substring(0, maxLength) + '...'
		},

		scrollToTop() {
			window.scrollTo(0, 0)
		},

		getModuleLink(module) {
			if (module?.id) {
				return {
					name: 'VideoLessons',
					query: { moduleId: module.id, courseId: module.courseId },
				}
			}
			return '/video-lessons'
		},
	},
}
</script>

<style scoped>
.view-587 {
	background-color: var(--white);
	border-radius: 20px;
	display: flex;
	flex-direction: column;
	min-height: 400px;
	max-height: 650px;
	width: 819px;
	overflow: hidden;
}

.x-47 {
	display: flex;
	flex-direction: column;
	min-height: 349px;
	max-height: 600px;
	margin-left: 35px;
	margin-top: 25px;
	margin-right: 20px;
	padding-bottom: 20px;
}

/* Заголовок + Фильтр */
.header-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	flex-shrink: 0;
}

.text-873 {
	letter-spacing: 1.54px;
	line-height: normal;
}

/* Фильтр курсов */
.course-filter {
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-label {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	color: #666;
}

.course-select {
	padding: 8px 12px;
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	color: #333;
	background-color: #fff;
	cursor: pointer;
	min-width: 180px;
	max-width: 250px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.course-select:hover {
	border-color: #4caf50;
}

.course-select:focus {
	outline: none;
	border-color: #4caf50;
	box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

/* Заголовки столбцов */
.columns-header {
	display: flex;
	align-items: center;
	padding: 10px 0;
	border-bottom: 1px solid #e0e0e0;
	margin-bottom: 10px;
	flex-shrink: 0;
}

.text-856 {
	width: 322px;
	letter-spacing: 0;
	line-height: normal;
}

.text-857 {
	width: 130px;
	margin-left: 70px;
	letter-spacing: 0;
	line-height: normal;
}

.column-spacer {
	width: 130px;
	margin-left: 70px;
	height: 24px;
	flex-shrink: 0;
}

.text-855 {
	margin-left: auto;
	letter-spacing: 0;
	line-height: normal;
}

/* Список модулей */
.modules-list {
	overflow-y: auto;
	overflow-x: hidden;
	flex: 1;
}

/* Стилизация скроллбара */
.modules-list::-webkit-scrollbar {
	width: 6px;
}

.modules-list::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 10px;
}

.modules-list::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 10px;
}

.modules-list::-webkit-scrollbar-thumb:hover {
	background: #a1a1a1;
}

/* Группа курса */
.course-group {
	margin-bottom: 15px;
}

.course-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 12px;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	margin-bottom: 8px;
	border-left: 4px solid #4caf50;
}

.course-icon {
	font-size: 18px;
}

.course-name {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #2c3e50;
}

.course-count {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 13px;
	color: #888;
}

/* Строка модуля */
.module-row {
	display: flex;
	align-items: center;
	padding: 12px 8px;
	border-radius: 8px;
	transition: background-color 0.2s ease;
}

.module-row:hover {
	background-color: #f5f5f5;
}

.module-text {
	width: 370px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.4;
}

.span-24 {
	letter-spacing: 0;
}

.span1-18 {
	letter-spacing: 0.29px;
}

.module-date {
	width: 130px;
	margin-left: 70px;
	letter-spacing: 0;
	line-height: normal;
	color: #666;
}

.module-date--spacer {
	color: transparent;
}

.module-status {
	margin-left: auto;
	height: 40px;
	width: 150px;
}

.status-btn {
	position: relative;
	width: 150px;
	height: 40px;
	display: block;
	text-decoration: none;
}

.btn-bg {
	border-radius: 10px;
	height: 40px;
	left: 0;
	position: absolute;
	top: 0;
	width: 150px;
	transition: box-shadow 0.2s ease;
}

.btn-bg--green {
	background-color: var(--shamrock, #4caf50);
	cursor: pointer;
}

.btn-bg--gray {
	background-color: #636363;
	cursor: not-allowed;
}

.btn-text {
	left: 29px;
	letter-spacing: 0;
	line-height: normal;
	position: absolute;
	top: 8px;
	width: 92px;
	text-align: center;
	color: white;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 18px;
	font-weight: 600;
}

.status-btn--available:hover .btn-bg--green {
	box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
}

.status-btn--available {
	transition: transform 0.2s ease;
}

.status-btn--available:hover {
	transform: translateY(-2px);
}

.status-btn--available:active {
	transform: translateY(0);
}

.loading-state,
.empty-state {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 150px;
	color: #666;
	font-size: 16px;
	font-family: 'Poppins', Helvetica, sans-serif;
}
</style>
